import { useId, useState } from "react";
import {
  MAX_SERIES_DAYS,
  daysBetween,
  isSeriesDate,
  type IsoDate,
} from "@estelle/domain";

import { DateRangePicker } from "@/components/dates/DateRangePicker";
import { Button } from "@/components/ui/button";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { RANGE_TOO_WIDE, START_AFTER_END } from "@/modules/reports/orderChart";

/** A named range of the store's days, as the buttons offer it. */
export interface DateRangePreset {
  id: string;
  label: string;
  from: IsoDate;
  to: IsoDate;
}

type Range = { from: IsoDate; to: IsoDate };

interface DateRangeControlsProps {
  /** Prefixes the picker's id, so two of these on a page do not share labels. */
  idPrefix: string;
  range: Range;
  /** The store's calendar date now: the latest day anyone can pick. */
  today: IsoDate;
  presets: readonly DateRangePreset[];
  onChange: (range: Range) => void;
  /**
   * Why the range on screen is not the one that was asked for — a link's range
   * the report could not use. Shown beside the picker until the next change.
   */
  notice?: string | null;
  /** In a report header the controls sit against the right edge (#639). */
  align?: "start" | "end";
}

/**
 * The dates a report is for: presets, and a range to pick or type.
 *
 * Plain controls over a range the caller keeps — in the address bar, so a
 * report can be linked to. A preset is highlighted only while the dates are
 * exactly what it means today, so "Last 30 days" stops being lit the day after
 * it was clicked.
 *
 * Dates are the store's, midnight to midnight, as `from` and `to` are to the
 * API: they name days, never instants, so no browser's timezone is involved.
 *
 * The picker hands over a range only when it has both ends (#555), so the
 * caller never hears of half of one. A range the report cannot use says why
 * and changes nothing, so the report stays on the range it has.
 */
export function DateRangeControls({
  idPrefix,
  range,
  today,
  presets,
  onChange,
  notice = null,
  align = "start",
}: DateRangeControlsProps) {
  // The range the problem was last about: when the caller's moves on its own
  // (a preset, the back button), what was wrong with the old pick is gone.
  const [seen, setSeen] = useState<Range>({ from: range.from, to: range.to });
  const [problem, setProblem] = useState<string | null>(null);
  const messageId = useId();

  if (seen.from !== range.from || seen.to !== range.to) {
    setSeen({ from: range.from, to: range.to });
    setProblem(null);
  }

  const selected = presets.find(
    (preset) => preset.from === range.from && preset.to === range.to,
  )?.id;

  function commit({ from, to }: { from: string; to: string }) {
    if (!isSeriesDate(from) || !isSeriesDate(to)) return;
    if (from > to) {
      setProblem(START_AFTER_END);
      return;
    }
    if (daysBetween(from, to) + 1 > MAX_SERIES_DAYS) {
      setProblem(RANGE_TOO_WIDE);
      return;
    }
    setProblem(null);
    if (from !== range.from || to !== range.to) onChange({ from, to });
  }

  const message = problem ?? notice;
  const id = `${idPrefix}-range`;

  return (
    <div
      className={cn("flex flex-col gap-2", align === "end" && "lg:items-end")}
    >
      <div
        className={cn(
          "flex flex-wrap items-end gap-x-3 gap-y-2",
          align === "end" && "lg:flex-col lg:items-end",
        )}
      >
        <div className="grid gap-1">
          <Label htmlFor={id} className="text-xs">
            Dates
          </Label>
          <DateRangePicker
            id={id}
            className="h-8"
            from={range.from}
            to={range.to}
            max={today}
            aria-invalid={problem !== null}
            aria-describedby={message ? messageId : undefined}
            onChange={commit}
          />
        </div>

        <div
          role="group"
          aria-label="Date range presets"
          className={cn(
            "flex flex-wrap gap-1",
            align === "end" && "lg:justify-end",
          )}
        >
          {presets.map((preset) => (
            <Button
              key={preset.id}
              type="button"
              size="xs"
              variant="outline"
              aria-pressed={selected === preset.id}
              className={cn(
                "h-8 px-2.5",
                selected === preset.id && SELECTED_CHOICE,
              )}
              onClick={() => {
                setProblem(null);
                if (preset.from !== range.from || preset.to !== range.to) {
                  onChange({ from: preset.from, to: preset.to });
                }
              }}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {message ? (
        <p id={messageId} role="alert" className="text-destructive text-xs">
          {message}
        </p>
      ) : null}
    </div>
  );
}
